<template lang="pug">
el-drawer(
  v-model="visible",
  :title="previewTicket?.name ?? ''",
  body-class="ticket-preview-drawer__body",
  header-class="ticket-preview-drawer__header",
  direction="rtl",
  :with-header="false",
  :z-index="999",
  :style="{ top: '63px', height: 'calc(100dvh - 63px)' }",
  size="60%"
)
  .h-full(v-if="previewLoading", v-loading="true")
  component(
    v-else-if="previewTicket !== null && ticketView",
    :is="ticketView",
    :key="previewTicket.uuid",
    :ticket="previewTicket",
    preview
  )
  TwinManagementDetail(
    v-else-if="previewTicket !== null && isAssetPreview",
    preview,
    :id="previewTicket.uuid",
    :template="previewTicket.templateUuid",
    :twin="previewTicket",
    :settings="assetManagementSettings"
  )
  .p-8(v-else-if="previewTicket !== null")
    h1.mb-8 {{ previewTicket.name }}
    TwinPropertiesView(:twin="previewTicket")
</template>

<script setup lang="ts">
import { computed, onMounted, provide, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useMq } from "vue3-mq";
import type { TwinResponse } from "@shared/services/twinManagement";
import { getTicketView } from "./ticketViewRegistry";
import { useTwinStore } from "@shared/stores/twinManagement";
import { ticketPreviewNavigationKey } from "./ticketPreviewNavigation";
import TwinPropertiesView from "@shared/components/twinManagement/components/TwinPropertiesView.vue";
import TwinManagementDetail from "@shared/components/twinManagement/TwinManagementDetail.vue";
import { prefix as assetManagementPrefix } from "@shared/assetManagement/constants";
import { assetManagementSettings } from "@shared/assetManagement/views/assetManagementSettings";

const props = defineProps<{
  ticket: TwinResponse | null;
  loading?: boolean;
  syncRoute?: boolean;
}>();

const visible = defineModel<boolean>({ required: true });
const twinStore = useTwinStore();
const route = useRoute();
const router = useRouter();
const mq = useMq();
const previewTicketUuid = ref<string | null>(props.ticket?.uuid ?? null);
const isReplacingPreviewTicket = ref(false);
const isNestedPreview = ref(false);
let latestPreviewRequest = 0;

watch(
  () => props.ticket,
  (ticket) => {
    if (
      props.syncRoute &&
      route.query.ticketPreview &&
      route.query.ticketPreview !== ticket?.uuid
    ) {
      return;
    }
    if (!isNestedPreview.value) {
      previewTicketUuid.value = ticket?.uuid ?? null;
    }
  },
);

watch(visible, (isVisible) => {
  if (!isVisible) {
    isNestedPreview.value = false;
  }
});

const previewTicketLoadingState = computed(() =>
  previewTicketUuid.value
    ? twinStore.getTwinLoadingState(previewTicketUuid.value)
    : null,
);

const previewTicket = computed(() => {
  const cachedTicket = previewTicketLoadingState.value?.result;
  if (cachedTicket?.uuid === previewTicketUuid.value) {
    return cachedTicket;
  }
  return props.ticket?.uuid === previewTicketUuid.value ? props.ticket : null;
});

const previewLoading = computed(
  () =>
    isReplacingPreviewTicket.value ||
    previewTicketLoadingState.value?.loading ||
    (props.ticket?.uuid === previewTicketUuid.value && props.loading),
);

async function replacePreviewTicket(ticketUuid: string) {
  const request = ++latestPreviewRequest;
  isNestedPreview.value = true;
  previewTicketUuid.value = ticketUuid;
  isReplacingPreviewTicket.value = true;
  try {
    if (props.syncRoute && route.query.ticketPreview !== ticketUuid) {
      await router.push({
        name: route.name,
        params: route.params,
        query: ticketPreviewQuery(ticketUuid),
        hash: route.hash,
      });
    }
    await twinStore.loadTwinCached(ticketUuid);
  } catch (e) {
    if (request === latestPreviewRequest) {
      throw e;
    }
  } finally {
    if (request === latestPreviewRequest) {
      isReplacingPreviewTicket.value = false;
    }
  }
}

function ticketPreviewQuery(ticketUuid?: string) {
  const query = { ...route.query };
  if (ticketUuid) {
    query.ticketPreview = ticketUuid;
  } else {
    delete query.ticketPreview;
  }
  return query;
}

function navigateToTicketDetail(ticketUuid: string) {
  void router.replace({
    name: "redirectTwin",
    params: { id: ticketUuid },
    query: ticketPreviewQuery(),
  });
}

function isPreviousPreviewOnCurrentRoute() {
  const previousUrl = window.history.state.back;
  if (typeof previousUrl !== "string") return false;

  const previous = new URL(previousUrl, window.location.origin);
  return (
    previous.pathname === window.location.pathname &&
    previous.searchParams.has("ticketPreview")
  );
}

function closePreview() {
  if (!props.syncRoute || !route.query.ticketPreview) return;

  if (isPreviousPreviewOnCurrentRoute()) {
    router.back();
    return;
  }

  void router.replace({
    name: route.name,
    params: route.params,
    query: ticketPreviewQuery(),
    hash: route.hash,
  });
}

async function syncPreviewFromRoute(ticketUuid: string | undefined) {
  if (!props.syncRoute) return;

  if (!ticketUuid) {
    visible.value = false;
    return;
  }

  if (!mq.mdPlus) {
    navigateToTicketDetail(ticketUuid);
    return;
  }

  visible.value = true;
  if (
    isReplacingPreviewTicket.value &&
    previewTicketUuid.value === ticketUuid
  ) {
    return;
  }

  isNestedPreview.value = true;
  previewTicketUuid.value = ticketUuid;
  if (previewTicketLoadingState.value?.result?.uuid === ticketUuid) {
    return;
  }

  const request = ++latestPreviewRequest;
  isReplacingPreviewTicket.value = true;
  try {
    await twinStore.loadTwinCached(ticketUuid);
  } finally {
    if (request === latestPreviewRequest) {
      isReplacingPreviewTicket.value = false;
    }
  }
}

watch(
  () => route.query.ticketPreview,
  (ticketPreview) => {
    void syncPreviewFromRoute(
      typeof ticketPreview === "string" ? ticketPreview : undefined,
    );
  },
);

watch([visible, () => props.ticket], ([isVisible, ticket]) => {
  if (!props.syncRoute || !isVisible || !ticket) return;
  if (route.query.ticketPreview === ticket.uuid) return;

  isNestedPreview.value = false;
  previewTicketUuid.value = ticket.uuid;
  void router.push({
    name: route.name,
    params: route.params,
    query: ticketPreviewQuery(ticket.uuid),
    hash: route.hash,
  });
});

watch(visible, (isVisible) => {
  if (!isVisible) {
    closePreview();
  }
});

onMounted(() => {
  void syncPreviewFromRoute(
    typeof route.query.ticketPreview === "string"
      ? route.query.ticketPreview
      : undefined,
  );
});

provide(ticketPreviewNavigationKey, replacePreviewTicket);

const ticketView = computed(() => getTicketView(previewTicket.value?.type));
const isAssetPreview = computed(() => {
  const type = previewTicket.value?.type;
  return (
    type === assetManagementPrefix.slice(0, -1) ||
    type?.startsWith(assetManagementPrefix)
  );
});
</script>

<style>
.ticket-preview-drawer__body {
  background-color: var(--color-background);
}
</style>
